import type { SeedJourneySuggestion, SeedPlan, SeedProject } from "../../../api/lifeOs";
import type { CaptureLocale, CommitRequest } from "../../../api/capture";
import type { LifeProjectConfig } from "./LifeProjects";

/** Everything the seeder learned about a project that the room needs later —
 *  previously only lens+status survived materialization. */
export function buildLifeProjectConfig(
  pr: SeedProject,
  spaceIdByName: Record<string, string>,
  journeySuggestions: SeedJourneySuggestion[],
  wantsActive: boolean
): LifeProjectConfig {
  const cfg: LifeProjectConfig = {
    lens: pr.lens,
    status: wantsActive ? "promote" : "someday",
  };
  if (pr.whyNow) cfg.whyNow = pr.whyNow;
  if (pr.spaceName) {
    cfg.spaceName = pr.spaceName;
    const spaceId = spaceIdByName[pr.spaceName];
    if (spaceId) cfg.spaceId = spaceId;
  }
  const suggestion = journeySuggestions.find((j) => j.projectTitle === pr.title);
  if (suggestion) cfg.journeySuggestedReason = suggestion.reason;
  return cfg;
}

/** Ticked seeder suggestions → one smart-capture commit each (spec 2026-11-02 §6).
 *  A quality whose space doesn't exist (yet) is saved without one. */
export function buildSeedSuggestionCommits(
  plan: Pick<SeedPlan, "mentalModels" | "qualities">,
  sel: { mentalModels: boolean[]; qualities: boolean[] },
  spaceIdByName: Record<string, string>,
  locale: CaptureLocale
): CommitRequest[] {
  const out: CommitRequest[] = [];
  (plan.mentalModels ?? []).forEach((m, i) => {
    const text = m.statement.trim();
    if (!sel.mentalModels[i] || text.length < 3) return;
    out.push({ text, source: "seeder", locale, whole: { text, kind: "mental_model", mmType: m.mmType }, parts: [] });
  });
  (plan.qualities ?? []).forEach((q, i) => {
    const text = q.name.trim();
    if (!sel.qualities[i] || text.length < 3) return;
    const spaceId = q.spaceName ? spaceIdByName[q.spaceName] : undefined;
    out.push({ text, source: "seeder", locale, whole: { text, kind: "quality", ...(spaceId ? { spaceId } : {}) }, parts: [] });
  });
  return out;
}
